import type { Severity } from '../engine/types';
import { KNOT, KNOT_BANG, KNOT_CENTRE, KNOT_TAIL } from './icons';

const SEVERITY_COLOR: Record<Severity, string> = {
  error: 'text-error',
  warning: 'text-warning',
  info: 'text-info',
};

/** Knotted-yarn dot: solid knot with "!" (error), open ring (warning), small knot (info). */
export function SeverityIcon({ severity, size = 14, className = '' }: { severity: Severity; size?: number; className?: string }) {
  const k = KNOT[severity];
  const c = KNOT_CENTRE;
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 ${SEVERITY_COLOR[severity]} ${className}`}
    >
      {k.disc && <circle cx={c} cy={c} r={k.disc} fill="currentColor" />}
      {k.ring && <circle cx={c} cy={c} r={k.ring[0]} fill="none" stroke="currentColor" strokeWidth={k.ring[1]} />}
      {k.bang && <path d={KNOT_BANG} className="stroke-surface" strokeWidth={1.5} strokeLinecap="round" fill="none" />}
      <path d={KNOT_TAIL} fill="none" stroke="currentColor" strokeWidth={1.3} strokeLinecap="round" />
    </svg>
  );
}

const PATHS = {
  link: 'M6.9 9.1a3 3 0 004.2 0l2.4-2.4a3 3 0 00-4.2-4.2l-.9.9M9.1 6.9a3 3 0 00-4.2 0L2.5 9.3a3 3 0 004.2 4.2l.9-.9',
  download: 'M8 2v8m0 0l-3-3m3 3l3-3M3 12.5V14h10v-1.5',
  trash: 'M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 9h5.8l.6-9',
  plus: 'M8 3v10M3 8h10',
  minus: 'M3 8h10',
  fit: 'M2.5 6V2.5H6M10 2.5h3.5V6M13.5 10v3.5H10M6 13.5H2.5V10',
  lock: 'M4.5 7V5a3.5 3.5 0 017 0v2M3.5 7h9v6.5h-9z',
  info: 'M8 7.2v4.3M8 4.6v.2M8 1.5a6.5 6.5 0 110 13 6.5 6.5 0 010-13z',
  check: 'M3 8.5l3.2 3L13 4.5',
  copy: 'M5.5 5.5h7v8h-7zM3.5 10.5v-7h7',
  x: 'M4 4l8 8M12 4l-8 8',
  arrow: 'M6 3.5L10.5 8 6 12.5',
  arrowRight: 'M2.5 8h10.5M9.5 4.5 13 8l-3.5 3.5',
  chevronDown: 'M3.5 6 8 10.5 12.5 6',
  chevronUp: 'M3.5 10 8 5.5 12.5 10',
  book: 'M2.5 3.5h4a2 2 0 0 1 2 2v8a1.5 1.5 0 0 0-1.5-1.5h-4.5zM13.5 3.5h-4a2 2 0 0 0-1 .3M13.5 3.5V12H9a1.5 1.5 0 0 0-1.5 1.5',
  eye: 'M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8zM8 6.2a1.8 1.8 0 100 3.6 1.8 1.8 0 000-3.6z',
  more: 'M3.5 8h.01M8 8h.01M12.5 8h.01',
  print: 'M4.5 6V2.5h7V6M4.5 11.5h-2v-5h11v5h-2M4.5 9.5h7v4h-7z',
  undo: 'M5.5 3.5 2.5 6.5l3 3M2.5 6.5H10a3.5 3.5 0 010 7H7',
  flag: 'M3.5 14V2.5M3.5 3h8l-1.8 3 1.8 3h-8',
  sliders: 'M2.5 5h6M11.5 5h2M2.5 11h2M7.5 11h6M10 3.5v3M6 9.5v3',
  paste: 'M5.5 3H4a1 1 0 00-1 1v9.5a1 1 0 001 1h8a1 1 0 001-1V4a1 1 0 00-1-1h-1.5M5.5 2.5h5v2h-5zM5.5 8.5h5M5.5 11h3',
  table: 'M2.5 3.5h11v9h-11zM2.5 6.5h11M2.5 9.5h11M6.5 3.5v9',
  bars: 'M2.5 4h8M2.5 8h11M2.5 12h5.5',
  /** Theme toggle: a small sun (light) and a crescent (dark). */
  sun: 'M8 5.3a2.7 2.7 0 1 1 0 5.4 2.7 2.7 0 0 1 0-5.4zM8 1.6v1.2M8 13.2v1.2M1.6 8h1.2M13.2 8h1.2M3.5 3.5l.85.85M11.65 11.65l.85.85M3.5 12.5l.85-.85M11.65 4.35l.85-.85',
  moon: 'M13.2 8.8A5.6 5.6 0 1 1 7.2 2.8a4.6 4.6 0 0 0 6 6z',
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 14, className = '' }: { name: IconName; size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 ${className}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={name === 'more' ? 2.4 : 1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

export function Spinner({ size = 12 }: { size?: number }) {
  return (
    <svg viewBox="0 0 16 16" width={size} height={size} aria-hidden="true" className="shrink-0 animate-spin motion-reduce:animate-none">
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
      <path d="M14 8a6 6 0 00-6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Frogless mark: one knit stitch, the "V" of stockinette, drawn as two leaning loops of yarn in a
 * thin moss line (32 px grid). The line is thicker at small sizes so it holds at 16 px.
 */
export const LOGO_PATHS = {
  left: 'M16 27.2C11.1 24.4 7.4 17.5 8.7 6.4c4.7 1.6 7.1 7.2 7.3 20.8z',
  right: 'M16 27.2c4.9-2.8 8.6-9.7 7.3-20.8-4.7 1.6-7.1 7.2-7.3 20.8z',
} as const;

export function logoStroke(size: number): number {
  return size <= 20 ? 2.3 : size <= 28 ? 1.9 : 1.6;
}

export function Logo({ size = 28, className = '' }: { size?: number; className?: string }) {
  const w = logoStroke(size);
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden="true" className={`shrink-0 ${className}`}>
      <g className="stroke-accent" fill="none" strokeWidth={w} strokeLinejoin="round" strokeLinecap="round">
        <path d={LOGO_PATHS.left} />
        <path d={LOGO_PATHS.right} />
      </g>
    </svg>
  );
}

/** Wordmark: the mark, "Frogless" in the serif; the tagline in the sans on wide screens. */
export function Wordmark({ tagline, level }: { tagline: string; level?: string }) {
  return (
    <div className="flex items-center gap-2">
      <Logo size={26} />
      <div className="leading-none">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-[21px] font-[480] text-fg">Frogless</span>
          {level && <span className="kicker hidden xl:inline">{level}</span>}
        </div>
        <div className="mt-1 hidden text-2xs text-muted 2xl:block">{tagline}</div>
      </div>
    </div>
  );
}

/** Small original line icons for the example cards (24 px grid, 1.5 stroke). */
const GARMENT_PATHS = {
  hat: ['M5.5 15.5c0-5 2.9-8.6 6.5-8.6s6.5 3.6 6.5 8.6', 'M4.5 15.5h15v4h-15z', 'M7.5 15.5v4M10.5 15.5v4M13.5 15.5v4M16.5 15.5v4', 'M12 4.9a1.6 1.6 0 1 0 0-.1'],
  hatEyelet: ['M5.5 15.5c0-5 2.9-8.6 6.5-8.6s6.5 3.6 6.5 8.6', 'M4.5 15.5h15v4h-15z', 'M8.2 12.4h.1M12 12.4h.1M15.8 12.4h.1', 'M12 4.9a1.6 1.6 0 1 0 0-.1'],
  cable: ['M8.5 3c0 4.2 7 4.8 7 9s-7 4.8-7 9', 'M15.5 3c0 1.7-1.1 2.8-2.5 3.6M11 17.4c-1.4.8-2.5 1.9-2.5 3.6', 'M5 3v18M19 3v18'],
  sock: ['M9 3.5h6v9l3.6 3a2.7 2.7 0 0 1-1.7 4.8H11.5A4.5 4.5 0 0 1 7 15.8V3.5z', 'M9 6.5h6', 'M7 14.5c2.5 0 4 1.8 4 4.2'],
  vest: ['M8.5 3.5 12 10l3.5-6.5L19 6v14.5H5V6z', 'M5 10.5c1.8 0 3-1.4 3-3.4M19 10.5c-1.8 0-3-1.4-3-3.4', 'M12 12.5h.1M12 15.5h.1M12 18.5h.1'],
  mitten: ['M8.5 20.5V9.5A2.5 2.5 0 0 1 11 7h3.5A2.5 2.5 0 0 1 17 9.5v11z', 'M8.5 13.5 6 11.8a1.5 1.5 0 0 0-2 .4 1.5 1.5 0 0 0 .4 2l4.1 2.9', 'M8.5 17.5H17', 'M10.5 7V4.5M13 7V4M15.5 7V4.8'],
  scarf: ['M8 3.5h5v10.5l3.5 6.5h-5L8 14z', 'M10.5 20.5v1.5M13 20.5v1.5M15.5 20.5v1.5', 'M8 7h5M8 10.5h5'],
  pattern: ['M6 3.5h9l3 3v14H6z', 'M15 3.5v3h3', 'M9 10h6M9 13h6M9 16h4'],
  // The welcome's « Votre patron » actions: paste, open a file, start from a blank.
  clipboard: [
    'M9 4.5H7.5A1.5 1.5 0 0 0 6 6v13.5A1.5 1.5 0 0 0 7.5 21h9a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H15',
    'M9 3h6v3H9z',
    'M9 10.5h6M9 13.5h6M9 16.5h3.5',
  ],
  folder: ['M3.5 7.5A1.5 1.5 0 0 1 5 6h4.2l1.8 2H19a1.5 1.5 0 0 1 1.5 1.5V18a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 18z', 'M3.5 11h17'],
  pencil: ['M15.5 4.5l4 4L9 19H5v-4z', 'M13.5 6.5l4 4', 'M13 20.5h6.5'],
} as const;

export type GarmentIconName = keyof typeof GARMENT_PATHS;

export function GarmentIcon({ name, size = 22, className = '' }: { name: GarmentIconName; size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      className={`shrink-0 ${className}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {GARMENT_PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

/** Picks an icon from an example's id/title (FR or EN words). */
export function garmentFor(idOrTitle: string): GarmentIconName {
  const s = idOrTitle.toLowerCase();
  if (/œillet|oeillet|eyelet/.test(s)) return 'hatEyelet';
  if (/cable|torsade/.test(s)) return 'cable';
  if (/sock|chaussette|heel|talon/.test(s)) return 'sock';
  if (/vest|gilet|cardigan/.test(s)) return 'vest';
  if (/mitaine|mitt|glove|gant|moufle/.test(s)) return 'mitten';
  if (/scarf|écharpe|echarpe|snood/.test(s)) return 'scarf';
  if (/hat|bonnet|beanie|toque/.test(s)) return 'hat';
  return 'pattern';
}
